import { Form } from 'factorial-form'

import asyncAction from 'modules/core/lib/asyncAction'

import { ConditionTypes } from 'modules/automations/lib/enums'
import type { Action } from 'modules/automations/store/models/Action'
import {
  ITriggerCondition,
  ITriggerConditions,
  Trigger,
} from 'modules/automations/store/models/Trigger'

import type { Storeable } from './Storeable'

export type BranchOperation = {
  attribute: string
  id: number
  operator: string
  value: string
}

export class Branch implements Storeable {
  #action: Action
  #operation: BranchOperation
  condition: ITriggerCondition | undefined
  form: Form

  constructor(action: Action, conditions: ITriggerConditions[]) {
    this.#action = action
    this.#operation = this.#action.metadata.operations[0]
    this.condition = this.searchCondition(conditions)
    this.form = this._updateForm()
  }

  searchCondition(triggerConditions: ITriggerConditions[]) {
    const conditions = triggerConditions.flatMap((item) => item.conditions)

    return conditions.find((item) => {
      const attribute = this.#operation.attribute

      if (attribute === 'custom_fields') {
        return item.key === `${'custom_field'}_${this.#operation.id}`
      }

      return item.operations.some((item) => item.attribute === attribute)
    })
  }

  dispose() {}

  // This is not reactive ATM since it changes the schema. `factorial-form`
  // does not have a way to update the schema and UI-wise it does not
  // make much sense
  _updateForm() {
    const operation = this.#operation

    // TODO: domain_extension -> Remove Trigger.BooleanAttributes.includes when all triggers are migrated
    if (
      this.condition?.type === ConditionTypes.Date ||
      Trigger.DateAttributes.includes(operation.attribute)
    ) {
      return new Form(
        {
          operator: operation.operator,
          value: operation.value,
        },
        {
          operator: 'string',
          value: 'date',
        }
      )
    }

    if (
      this.condition?.type === ConditionTypes.Boolean ||
      Trigger.BooleanAttributes.includes(operation.attribute)
    ) {
      return new Form(
        {
          operator: operation.operator,
          value: operation.value,
        },
        {
          operator: 'string',
          value: 'boolean',
        }
      )
    }

    if (
      [String(ConditionTypes.Identifier), String(ConditionTypes.Number)].includes(
        this.condition?.type || ''
      )
    ) {
      return new Form(
        {
          operator: operation.operator,
          value: Number(operation.value),
        },
        {
          operator: 'string',
          value: 'number',
        }
      )
    }

    return new Form(
      {
        operator: operation.operator,
        value: operation.value,
      },
      {
        operator: 'string',
        value: 'string',
      }
    )
  }

  async save() {
    if (!this.form.isDirty) return

    const operation = this.#action.metadata.operations[0]

    const { success } = await asyncAction(() =>
      this.#action.save({
        metadata: {
          operations: [
            {
              attribute: operation.attribute,
              id: operation.id,
              ...this.form.data(),
            },
          ],
        },
      })
    )

    if (success) this.form.cleanAll()
  }
}
